import { useUser } from '@clerk/nextjs';
import { datadogLogs } from '@datadog/browser-logs';
import { logContext } from '@shared/services';
import { useQueryClient } from '@tanstack/react-query';
import { getQueryKey } from '@trpc/react-query';
import { useSupportConversationRefresh } from '@web-app/hooks/support-concierge/useSupportConversationRefresh';
import { useSupportConciergeEnabled } from '@web-app/utils/feature-flags/support-concierge';
import {
  PROVIDER_EVENT_BROWSER_EVENT,
  type ProviderEvent,
  SUPPORT_CONVERSATION_CHANGED_EVENT,
  SUPPORT_INVALIDATE_EVENT,
  supportConversationChangedSchema,
} from '@web-app/utils/provider-events/contract';
import { trpc } from '@web-app/utils/trpc';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { v4 as uuid } from 'uuid';

import { isProviderEventAllowed } from './page-policy';

type SupportRefresh = { type: string; cancelled: boolean };

/** One authenticated event stream per tab, independent of the current page. */
export function ProviderEvents() {
  const { user } = useUser();
  const { route } = useRouter();
  const currentRoute = useRef(route);
  currentRoute.current = route;
  const viewerId = user?.id ?? '';
  const currentViewer = useRef(viewerId);
  currentViewer.current = viewerId;
  const connection = useMemo<{
    correlationId: string;
    viewerId: string;
    state: string;
    connectionId?: string;
    reconnects: number;
    supportNeedsRefresh: boolean;
    refreshes: Set<SupportRefresh>;
  }>(
    () => ({
      correlationId: uuid(),
      viewerId,
      state: 'idle',
      reconnects: 0,
      supportNeedsRefresh: false,
      refreshes: new Set<SupportRefresh>(),
    }),
    [viewerId],
  );
  const supportEnabled = useSupportConciergeEnabled();
  const currentSupportEnabled = useRef(supportEnabled);
  currentSupportEnabled.current = supportEnabled;
  const utils = trpc.useUtils();
  const queryClient = useQueryClient();
  const refreshConversation = useSupportConversationRefresh(
    viewerId,
    supportEnabled &&
      isProviderEventAllowed(route, SUPPORT_CONVERSATION_CHANGED_EVENT),
  );
  const availability = trpc.providerEvents.availability.useQuery(
    { viewerId },
    {
      enabled: Boolean(viewerId),
      retry: false,
      staleTime: 60_000,
      gcTime: 0,
      refetchOnWindowFocus: true,
    },
  );
  const enabled = Boolean(viewerId) && availability.data?.enabled === true;
  useEffect(
    function observeProviderEventSubscription() {
      if (!enabled) return;
      datadogLogs.logger.info(
        'Provider event subscription requested',
        logContext('provider-events', 'connection', {
          schemaVersion: 1,
          layer: 'browser',
          stage: 'connection_requested',
          outcome: 'started',
          correlationId: connection.correlationId,
          subscriptionId: connection.correlationId,
          detail: 'Browser enabled the shared event subscription.',
        }),
      );
      return () => {
        connection.state = 'idle';
        datadogLogs.logger.info(
          'Provider event subscription stopped',
          logContext('provider-events', 'connection', {
            schemaVersion: 1,
            layer: 'browser',
            stage: 'connection_stopped',
            outcome: 'completed',
            correlationId: connection.correlationId,
            subscriptionId: connection.correlationId,
            connectionId: connection.connectionId,
            reason: 'scope_ended',
            detail:
              'Component, viewer scope or feature availability changed; tab termination may not flush this log.',
          }),
        );
      };
    },
    [enabled, connection],
  );

  const refreshSupport = useCallback(
    (trigger: 'event' | 'ready' | 'page_exit', event?: ProviderEvent) => {
      if (currentViewer.current !== viewerId || !currentSupportEnabled.current)
        return;
      const eventType = event?.type ?? SUPPORT_INVALIDATE_EVENT;
      if (!isProviderEventAllowed(currentRoute.current, eventType)) {
        connection.supportNeedsRefresh = true;
        return;
      }
      const targeted =
        !!event && event.type === SUPPORT_CONVERSATION_CHANGED_EVENT;
      const conversationId =
        targeted && event
          ? supportConversationChangedSchema.safeParse(event.payload).data
              ?.conversationId
          : undefined;
      const startedAt = performance.now();
      const context = logContext('provider-events', 'refresh', {
        schemaVersion: 1,
        layer: 'browser',
        stage: 'query_refresh',
        correlationId: event ? event.id : connection.correlationId,
        subscriptionId: connection.correlationId,
        connectionId: connection.connectionId,
        eventId: event?.id,
        trigger,
        messageType: event ? event.type : SUPPORT_INVALIDATE_EVENT,
        refreshScope: targeted ? 'conversation' : 'all',
        conversationId,
        detail:
          'Support refresh requested; targeted events fetch the affected conversation and update its cached inbox entry.',
      });
      if (targeted && !conversationId) {
        datadogLogs.logger.warn('Provider event payload rejected', {
          ...context,
          outcome: 'failed',
          reason: 'invalid_conversation_payload',
        });
        return;
      }
      const job: SupportRefresh = { type: eventType, cancelled: false };
      connection.refreshes.add(job);
      const operations =
        conversationId && event
          ? [
              // The direct read also updates notifications; fall back to their
              // lagging search only when it fails.
              refreshConversation(conversationId, event.id).catch((error) => {
                void utils.supportConcierge.unreadConversations.invalidate({
                  viewerId,
                });
                throw error;
              }),
            ]
          : [
              utils.supportConcierge.unreadConversations.invalidate({
                viewerId,
              }),
              utils.supportConcierge.availability.invalidate({
                viewerId,
              }),
              utils.supportConcierge.conversations.invalidate({
                viewerId,
              }),
              utils.supportConcierge.conversation.invalidate({
                viewerId,
              }),
            ];
      // Keep the group cancellable until every request finishes, including after a sibling fails.
      const refresh = Promise.allSettled(operations).then((results) => {
        const failed = results.find((result) => result.status === 'rejected');
        if (failed) throw failed.reason;
        const first = results[0];
        return first?.status === 'fulfilled' && first.value
          ? first.value
          : { outcome: 'completed' as const };
      });
      void refresh
        .then((result) => {
          datadogLogs.logger.info('Provider event query refresh completed', {
            ...context,
            ...result,
            ...(job.cancelled || currentViewer.current !== viewerId
              ? { outcome: 'skipped', reason: 'scope_ended' }
              : {}),
            durationMs: performance.now() - startedAt,
          });
        })
        .catch(() => {
          const skipped = job.cancelled || currentViewer.current !== viewerId;
          datadogLogs.logger[skipped ? 'info' : 'warn'](
            skipped
              ? 'Provider event query refresh skipped'
              : 'Provider event query refresh failed',
            {
              ...context,
              outcome: skipped ? 'skipped' : 'failed',
              reason: skipped ? 'scope_ended' : undefined,
              durationMs: performance.now() - startedAt,
            },
          );
        })
        .finally(() => connection.refreshes.delete(job));
    },
    [connection, viewerId, refreshConversation, utils],
  );

  const cancelSupportRefreshes = useCallback(() => {
    const pending = [...connection.refreshes].filter((job) => !job.cancelled);
    if (!pending.length) return;
    connection.supportNeedsRefresh = true;
    for (const job of pending) job.cancelled = true;
    // These query keys belong only to support. The room's queries and SSE remain active.
    const keys = [
      getQueryKey(
        trpc.supportConcierge.unreadConversations,
        { viewerId },
        'query',
      ),
      getQueryKey(trpc.supportConcierge.availability, { viewerId }, 'query'),
      getQueryKey(
        trpc.supportConcierge.conversations,
        { viewerId },
        'infinite',
      ),
      getQueryKey(trpc.supportConcierge.conversation, { viewerId }, 'query'),
    ];
    void Promise.all(
      keys.map((queryKey) => queryClient.cancelQueries({ queryKey })),
    ).catch(() => {
      datadogLogs.logger.warn(
        'Provider event refresh cancellation failed',
        logContext('provider-events', 'refresh', {
          schemaVersion: 1,
          layer: 'browser',
          stage: 'refresh_cancellation',
          outcome: 'failed',
          correlationId: connection.correlationId,
          subscriptionId: connection.correlationId,
          detail:
            'Support refresh cancellation failed after the processing scope ended.',
        }),
      );
    });
  }, [connection, queryClient, viewerId]);

  useEffect(
    function applyPageEventPolicy() {
      if (
        [...connection.refreshes].some(
          (job) => !job.cancelled && !isProviderEventAllowed(route, job.type),
        )
      ) {
        cancelSupportRefreshes();
      }
      if (
        viewerId &&
        supportEnabled &&
        connection.supportNeedsRefresh &&
        isProviderEventAllowed(route, SUPPORT_INVALIDATE_EVENT)
      ) {
        // Consume once. Repeated hints/ready packets never accumulate a replay queue.
        connection.supportNeedsRefresh = false;
        refreshSupport('page_exit');
      }
    },
    [
      route,
      viewerId,
      supportEnabled,
      connection,
      cancelSupportRefreshes,
      refreshSupport,
    ],
  );

  useEffect(
    function cancelEndedViewerRefreshes() {
      return cancelSupportRefreshes;
    },
    [cancelSupportRefreshes],
  );

  const subscription = trpc.providerEvents.subscribe.useSubscription(
    { viewerId, correlationId: connection.correlationId },
    {
      enabled,
      onError(error) {
        if (currentViewer.current !== viewerId) return;
        datadogLogs.logger.error(
          'Provider event subscription failed',
          logContext('provider-events', 'connection', {
            schemaVersion: 1,
            layer: 'browser',
            stage: 'connection_failed',
            outcome: 'failed',
            correlationId: connection.correlationId,
            subscriptionId: connection.correlationId,
            connectionId: connection.connectionId,
            errorCode: error.data?.code ?? 'TRANSPORT_ERROR',
            detail:
              'The browser subscription ended with an error; existing query refresh paths remain available.',
          }),
        );
      },
      onData(packet) {
        if (currentViewer.current !== viewerId) return;
        const previousConnectionId = connection.connectionId;
        connection.connectionId = packet.connectionId;
        if (packet.kind === 'ready') {
          datadogLogs.logger.info(
            'Provider event stream ready in browser',
            logContext('provider-events', 'connection', {
              schemaVersion: 1,
              layer: 'browser',
              stage: 'connection_established',
              outcome: 'completed',
              correlationId: connection.correlationId,
              subscriptionId: connection.correlationId,
              connectionId: packet.connectionId,
              providerId: packet.providerId,
              reason:
                previousConnectionId &&
                previousConnectionId === packet.connectionId
                  ? 'broker_resynchronized'
                  : 'stream_ready',
              detail:
                'Browser received the readiness packet; the stream can receive events.',
            }),
          );
        } else {
          datadogLogs.logger.info(
            'Provider event received in browser',
            logContext('provider-events', 'delivery', {
              schemaVersion: 1,
              layer: 'browser',
              stage: 'browser_received',
              outcome: 'completed',
              correlationId: packet.event.id,
              subscriptionId: connection.correlationId,
              connectionId: packet.connectionId,
              eventId: packet.event.id,
              providerId: packet.event.providerId,
              messageType: packet.event.type,
              detail:
                'Browser onData received the event; this does not establish that a provider viewed it.',
            }),
          );
          if (
            !isProviderEventAllowed(currentRoute.current, packet.event.type)
          ) {
            if (
              currentSupportEnabled.current &&
              (packet.event.type === SUPPORT_INVALIDATE_EVENT ||
                packet.event.type === SUPPORT_CONVERSATION_CHANGED_EVENT)
            ) {
              connection.supportNeedsRefresh = true;
            }
            datadogLogs.logger.info(
              'Provider event suppressed on restricted page',
              logContext('provider-events', 'delivery', {
                schemaVersion: 1,
                layer: 'browser',
                stage: 'event_suppressed',
                outcome: 'skipped',
                reason: 'restricted_page',
                routePattern: currentRoute.current,
                eventId: packet.event.id,
                messageType: packet.event.type,
                correlationId: packet.event.id,
                subscriptionId: connection.correlationId,
                connectionId: packet.connectionId,
                detail:
                  'Event received but not dispatched or processed on this page; no payload is queued.',
              }),
            );
            return;
          }
          window.dispatchEvent(
            new CustomEvent(PROVIDER_EVENT_BROWSER_EVENT, {
              detail: packet.event,
            }),
          );
        }
        if (
          currentSupportEnabled.current &&
          (packet.kind === 'ready' ||
            packet.event.type === SUPPORT_INVALIDATE_EVENT ||
            packet.event.type === SUPPORT_CONVERSATION_CHANGED_EVENT)
        ) {
          if (
            packet.kind === 'ready' &&
            !isProviderEventAllowed(
              currentRoute.current,
              SUPPORT_INVALIDATE_EVENT,
            )
          ) {
            datadogLogs.logger.info(
              'Provider event readiness refresh suppressed',
              logContext('provider-events', 'refresh', {
                schemaVersion: 1,
                layer: 'browser',
                stage: 'refresh_suppressed',
                outcome: 'skipped',
                reason: 'restricted_page',
                routePattern: currentRoute.current,
                trigger: 'ready',
                correlationId: connection.correlationId,
                subscriptionId: connection.correlationId,
                connectionId: packet.connectionId,
                detail:
                  'Stream remains connected; support refresh waits until this page permits it.',
              }),
            );
          }
          refreshSupport(
            packet.kind,
            packet.kind === 'event' ? packet.event : undefined,
          );
        }
      },
    },
  );
  const status = subscription.status;
  useEffect(
    function observeProviderEventConnectionState() {
      if (!enabled || currentViewer.current !== viewerId) return;
      if (status === 'connecting' && connection.state === 'pending') {
        connection.reconnects++;
        datadogLogs.logger.info(
          'Provider event connection interrupted',
          logContext('provider-events', 'connection', {
            schemaVersion: 1,
            layer: 'browser',
            stage: 'connection_interrupted',
            outcome: 'started',
            correlationId: connection.correlationId,
            subscriptionId: connection.correlationId,
            connectionId: connection.connectionId,
            reconnectCount: connection.reconnects,
            detail:
              'Browser is reconnecting; check the server close reason to distinguish scheduled renewal from interruption.',
          }),
        );
      }
      connection.state = status;
    },
    [status, enabled, viewerId, connection],
  );
  return null;
}
